import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import CitySearchSelect from "../../commons/CitySearchSelect/CitySearchSelect";
import {
  sanitizeLocationInput,
  sanitizePincodeInput,
  validateLocationName,
  validatePincode,
  formatPincodeForApi,
} from "../../utils/pincodeValidation";

const emptyCity = () => ({ id: "", name: "" });

const LocationPincodeModal = ({
  show,
  onHide,
  onSubmit,
  submitting = false,
  mode = "add",
  initialCity = emptyCity(),
  initialLocation = "",
  initialPincode = "",
}) => {
  const [city, setCity] = useState(emptyCity());
  const [location, setLocation] = useState("");
  const [pincode, setPincode] = useState("");
  const [errors, setErrors] = useState({ location: "", pincode: "" });

  useEffect(() => {
    if (!show) return;
    setCity(initialCity?.id ? initialCity : emptyCity());
    setLocation(initialLocation || "");
    setPincode(sanitizePincodeInput(initialPincode || ""));
    setErrors({ location: "", pincode: "" });
  }, [show, initialCity, initialLocation, initialPincode]);

  const handleLocationChange = (e) => {
    setLocation(sanitizeLocationInput(e.target.value));
    if (errors.location) {
      setErrors((prev) => ({ ...prev, location: "" }));
    }
  };

  const handlePincodeChange = (e) => {
    setPincode(sanitizePincodeInput(e.target.value));
    if (errors.pincode) {
      setErrors((prev) => ({ ...prev, pincode: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const locationError = validateLocationName(location);
    const pincodeError = validatePincode(pincode);

    if (locationError || pincodeError) {
      setErrors({
        location: locationError || "",
        pincode: pincodeError || "",
      });
      return;
    }

    onSubmit({
      city,
      location: location.trim().replace(/\s+/g, " "),
      pincode: formatPincodeForApi(pincode),
    });
  };

  const isEdit = mode === "edit";

  return (
    <Modal show={show} onHide={onHide} centered className="pincode-modal">
      <Modal.Header closeButton className="pincode-modal__header">
        <Modal.Title>{isEdit ? "Edit location & pincode" : "Add location & pincode"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pincode-modal__body">
        <form onSubmit={handleSubmit}>
          {isEdit ? (
            <div className="form-group mb-3">
              <input
                type="text"
                className="form-control default pincode-modal__city-readonly"
                value={city.name || ""}
                readOnly
              />
            </div>
          ) : (
            <div className="form-group mb-3">
              <CitySearchSelect
                label=""
                placeholder="Select city"
                value={city}
                onChange={setCity}
              />
            </div>
          )}
          <div className="form-group mb-3">
            <input
              type="text"
              className={`form-control default ${errors.location ? "is-invalid" : ""}`}
              placeholder="Enter location (letters only)"
              value={location}
              onChange={handleLocationChange}
              autoComplete="off"
            />
            {errors.location && <div className="invalid-feedback d-block">{errors.location}</div>}
          </div>
          <div className="form-group mb-4">
            <input
              type="text"
              className={`form-control default ${errors.pincode ? "is-invalid" : ""}`}
              placeholder="Enter pincode"
              value={pincode}
              onChange={handlePincodeChange}
              autoComplete="off"
              style={{ textTransform: "uppercase" }}
            />
            {errors.pincode && <div className="invalid-feedback d-block">{errors.pincode}</div>}
          </div>
          <div className="d-flex justify-content-end">
            <Button
              type="button"
              variant="secondary"
              className="mr-2 rounded-lg"
              onClick={onHide}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" className="rounded-lg" disabled={submitting}>
              {submitting ? "Saving..." : isEdit ? "Update" : "Add"}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default LocationPincodeModal;
